import { useState, useEffect, useContext } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { isLeft } from "fp-ts/lib/Either";
import LocalizationContext from "../../../providers/localization/LocalizationContext";
import ModalsContext from "../../../providers/modal/ModalsContext";
import KeyWordLocalization from "../../../utils/KeyWordLocalization";
import routes from "../../../routes/Routes";
import SearchResultEntity from "../../../../domain/entities/SearchResultEntity";
import ShiftEntity, { ShiftStatus } from "../../../../domain/entities/ShiftEntity";
import di from "../../../../DependencyInjection";
import SearchShiftsUseCase, { SearchShiftsUseCaseName } from "../../../../domain/use_cases/shift/SearchShiftsUseCase";
import DeleteShiftUseCase, { DeleteShiftUseCaseName } from "../../../../domain/use_cases/shift/DeleteShiftUseCase";
import ReasignShiftUseCase, { ReasignShiftUseCaseName } from "../../../../domain/use_cases/shift/ReasignShiftUseCase";
import CreateShiftComponent from "./form/create_shift.component";
import ReasignShiftModal from "./reasign/ReasignShiftModal";
import DominicanTime from "../../../../data/dto/DominicanTime";
import ShiftUpdateEntity from "../../../../domain/entities/ShiftUpdateEntity";

const _todayDateString = (): string => {
    const today = DominicanTime.nowDominicanTime();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const useShiftListViewModel = () => {
    const [searchParams] = useSearchParams();
    const searchQuery = searchParams.get("search");
    const pageQuery = parseInt(searchParams.get("page") ?? "1");
    const dateQuery = searchParams.get("date") ?? _todayDateString();
    const sortQuery = searchParams.get("sort") ?? "date";

    const { i18n } = useContext(LocalizationContext);
    const { addToast, openModalDelete, openModalCustom, closeModalCustom, closeModalDelete } = useContext(ModalsContext);
    const { register, handleSubmit, getValues, setValue, watch, formState: { errors } } = useForm();
    const [result, setResult] = useState<SearchResultEntity<ShiftEntity> | undefined>(undefined);
    const [loadingDownload, setLoadingDownload] = useState(false);
    const navigate = useNavigate();
    const itemsPerPage = 20;
    let _timerTap: any;

    const _searchShifts = async () => {
        const data = getValues();
        clearTimeout(_timerTap);
        setResult(undefined);
        const response = await di.get<SearchShiftsUseCase>(SearchShiftsUseCaseName).call({
            query: data["query"],
            page: data["currentPage"] || 1,
            itemsPerPage,
            date: new Date(data["date"] || dateQuery),
            sortByNeedAttendance: (data["sort"] || sortQuery) === "attention",
        });
        if (isLeft(response)) {
            setResult({
                data: [],
                page: 1,
                total: 0,
                itemsPerPage: 0,
                pages: 0,
            });
            return;
        }
        setResult(response.right);
    };

    const _handleSearch = (data: any) => {
        const search = data["query"];
        const page = data["currentPage"];
        const date = data["date"] || dateQuery;
        const sort = data["sort"] || sortQuery;
        let query = "?";
        if (search && search.length > 0) query += `search=${search}&`;
        if (page) query += `page=${page}&`;
        if (date) query += `date=${date}&`;
        if (sort) query += `sort=${sort}&`;
        if (query.endsWith("&")) query = query.slice(0, -1);
        navigate(routes.shift_list.relativePath + query);
    };

    const _handleChangeTextSearch = () => {
        clearTimeout(_timerTap);
        const data = getValues();
        _timerTap = setTimeout(() => _handleSearch(data), 500);
    };

    const _handleChange = () => {
        const data = getValues();
        _handleSearch(data);
    };

    const _handleChangeDate = (date: string) => {
        setValue("date", date);
        setValue("currentPage", 1);
        const data = getValues();
        _handleSearch(data);
    };

    const _handleChangeSort = (sort: string) => {
        setValue("sort", sort);
        setValue("currentPage", 1);
        const data = getValues();
        _handleSearch(data);
    };

    const _handleLoad = () => {
        setValue("query", searchQuery);
        setValue("currentPage", pageQuery);
        setValue("date", dateQuery);
        setValue("sort", sortQuery);
        _searchShifts();
    };

    const _handleOnChangePage = (page: number) => {
        setValue("currentPage", page);
        const data = getValues();
        _handleSearch(data);
    };

    const _handleOnClickDeleteShift = async (shift: ShiftEntity) => {
        const deleteShift = async () => {
            const response = await di.get<DeleteShiftUseCase>(DeleteShiftUseCaseName).call(shift.id);
            if (isLeft(response)) return addToast(response.left.code ?? KeyWordLocalization.UnknownError, 'error', undefined);
            closeModalDelete();
            addToast(i18n("Turno eliminado con éxito"), 'success', undefined);
            _searchShifts();
        };
        openModalDelete(
            i18n("Eliminar Turno"),
            i18n("¿Estás seguro de que deseas eliminar este turno?"),
            deleteShift
        );
    };

    const _handleOnClickUpdateShift = async (shift: ShiftEntity) => {
        const onUpdated = (updatedShift: ShiftEntity) => {
            closeModalCustom();
            _searchShifts();
        };
        openModalCustom(
            "lg",
            i18n("Editar Turno"),
            <CreateShiftComponent onUpdated={onUpdated} shift={shift} />
        );
    };

    const _handleOnCreateSingleShift = async () => {
        const onCreated = (createdShift: ShiftEntity) => {
            closeModalCustom();
            _searchShifts();
        };
        openModalCustom(
            "lg",
            i18n("Crear Turno"),
            <CreateShiftComponent onCreated={onCreated} />
        );
    };

    const _handleOnReasignShift = async (shift: ShiftEntity) => {
        const onReasign = (reasignedShift: ShiftEntity) => {
            closeModalCustom();
            _searchShifts();
        };
        openModalCustom(
            "lg",
            i18n("Reasignar Turno"),
            <ReasignShiftModal 
                shift={shift} 
                onReasign={onReasign} 
                onClose={closeModalCustom}
            />
        );
    };

    const _handleOnShowHistory = (changes: ShiftUpdateEntity[]) => {
        const content = (
            <div className="shift_history_modal">
                {changes.length === 0 ? (
                    <p className="text-muted">{i18n("Sin cambios registrados")}</p>
                ) : (
                    <ul className="list-group list-group-flush">
                        {changes.map((change, idx) => (
                            <li key={idx} className="list-group-item d-flex justify-content-between align-items-center">
                                <span className="badge bg-secondary me-2">{i18n(change.action)}</span>
                                {change.userName && <span className="text-muted small me-2">{change.userName}</span>}
                                <span className="text-muted small">{change.date.toLocaleString()}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        );
        openModalCustom("lg", i18n("Historial de cambios"), content);
    };

    const _canReasignShift = (shift: ShiftEntity): boolean => {
        const now = DominicanTime.nowDominicanTime();
        console.log("can reasign?", {
            "shift name": shift.id,
            "shift status": shift.status,
            "now": now,
            "shift end date": shift.endDate,
            "stablishment": shift.stablishment.name,
            "valet": shift.user.name,
        });
        // Se puede reasignar si:
        // - Está rechazado, no confirmado o pendiente
        // - Está rechazado por el supervisor
        // - No ha sido contactado por el supervisor
        // NO se puede reasignar si está contactado por el supervisor
        const canReasignStatuses = [
            ShiftStatus.REJECTED,
            ShiftStatus.NO_CONFIRMED,
            ShiftStatus.PENDING,
            ShiftStatus.REJECTED_BY_SUPERVISOR,
            ShiftStatus.NO_CONTACTED_BY_SUPERVISOR
        ];
        return canReasignStatuses.includes(shift.status) && now < shift.endDate && ([ShiftStatus.FINISHED, ShiftStatus.STARTED].includes(shift.status) === false);
    };

    useEffect(() => {
        _handleLoad();
    }, [searchQuery, pageQuery, dateQuery, sortQuery]);

    return {
        formInfo: { register, handleSubmit, getValues, setValue, watch, errors },
        result,
        loadingDownload,
        i18n,
        dateQuery,
        sortQuery,
        _handleSearch,
        _handleChangeTextSearch,
        _handleChange,
        _handleChangeDate,
        _handleChangeSort,
        _handleOnChangePage,
        _handleOnClickDeleteShift,
        _handleOnClickUpdateShift,
        _handleOnCreateSingleShift,
        _handleOnReasignShift,
        _handleOnShowHistory,
        _canReasignShift,
    };
};

export default useShiftListViewModel;